import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, CarIcon, SmartphoneIcon, PlaneIcon, CheckCircleIcon, ClockIcon, UploadCloudIcon, AlertCircleIcon, ArrowRightIcon, CalendarIcon, FileTextIcon, CameraIcon, ShieldCheckIcon, Loader2Icon } from 'lucide-react';
import { formatNaira, formatDate } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface ClaimsProps {
  navigate: (screen: string) => void;
}
type ClaimStep = 'list' | 'select' | 'details' | 'upload' | 'review' | 'processing' | 'success';
export function Claims({
  navigate
}: ClaimsProps) {
  const [step, setStep] = useState<ClaimStep>('list');
  const [selectedPolicy, setSelectedPolicy] = useState<string | null>(null);
  const [incidentDate, setIncidentDate] = useState('');
  const [incidentDesc, setIncidentDesc] = useState('');
  const [docsUploaded, setDocsUploaded] = useState(false);
  // Existing claims for the list view
  const existingClaims = [{
    id: 'CLM-8492',
    product: 'AutoGuard',
    icon: CarIcon,
    date: '2026-03-12',
    amount: 500000,
    status: 'review',
    statusText: 'Under Review',
    colorTheme: 'amber'
  }, {
    id: 'CLM-7102',
    product: 'TravelGuard',
    icon: PlaneIcon,
    date: '2026-02-20',
    amount: 120000,
    status: 'settled',
    statusText: 'Settled',
    colorTheme: 'emerald'
  }];
  // Mock active policies to claim against
  const activePolicies = [{
    id: 'LWY/MOT/2026/089',
    product: 'AutoGuard',
    icon: CarIcon,
    color: 'emerald'
  }, {
    id: 'COR/TRV/2026/142',
    product: 'TravelGuard',
    icon: PlaneIcon,
    color: 'blue'
  }];
  const getColorClasses = (theme: string) => {
    switch (theme) {
      case 'emerald':
        return {
          bg: 'bg-emerald-100',
          text: 'text-emerald-600',
          fill: 'bg-emerald-500',
          textFill: 'text-emerald-500'
        };
      case 'amber':
        return {
          bg: 'bg-amber-100',
          text: 'text-amber-600',
          fill: 'bg-amber-500',
          textFill: 'text-amber-500'
        };
      case 'blue':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-600',
          fill: 'bg-blue-500',
          textFill: 'text-blue-500'
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-600',
          fill: 'bg-gray-500',
          textFill: 'text-gray-500'
        };
    }
  };
  const handleBack = () => {
    if (step === 'list') navigate('policies');else if (step === 'select') setStep('list');else if (step === 'details') setStep('select');else if (step === 'upload') setStep('details');else if (step === 'review') setStep('upload');
  };
  const getTopBarTitle = () => {
    switch (step) {
      case 'list':
        return 'Claims';
      case 'select':
        return 'Select Policy';
      case 'details':
        return 'Incident Details';
      case 'upload':
        return 'Upload Evidence';
      case 'review':
        return 'Review Claim';
      default:
        return '';
    }
  };
  const pageVariants = {
    initial: {
      opacity: 0,
      x: 20
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -20
    }
  };
  const selectedPolicyDetails = activePolicies.find(p => p.id === selectedPolicy);
  // Simulate processing
  useEffect(() => {
    if (step === 'processing') {
      const timer = setTimeout(() => setStep('success'), 2500);
      return () => clearTimeout(timer);
    }
  }, [step]);
  return <div className="flex-1 overflow-hidden bg-slate-50 flex flex-col h-full relative" data-id="element-1199">
      {step !== 'processing' && step !== 'success' && <TopBar title={getTopBarTitle()} showBack onBack={handleBack} onNotificationClick={() => navigate('notifications')} data-id="element-1200" />}

      <div className="flex-1 relative overflow-hidden" data-id="element-1201">
        <AnimatePresence mode="wait" data-id="element-1202">
          {/* STEP 1: CLAIMS LIST */}
          {step === 'list' && <motion.div key="list" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="absolute inset-0 overflow-y-auto pb-safe px-5 pt-4" data-id="element-1203">
              <div className="bg-[#0D3B3B] rounded-3xl p-6 text-white mb-8 relative overflow-hidden shadow-xl shadow-emerald-900/20" data-id="element-1204">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-1205"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-emerald-500/20 rounded-full blur-xl -ml-10 -mb-10" data-id="element-1206"></div>

                <div className="relative z-10" data-id="element-1207">
                  <p className="text-emerald-400 text-xs font-bold uppercase tracking-widest mb-2" data-id="element-1208">
                    Claims Center
                  </p>
                  <h1 className="text-2xl font-bold mb-4" data-id="element-1209">
                    Need to make a claim?
                  </h1>
                  <p className="text-sm text-white/70 mb-6 max-w-[240px] leading-relaxed" data-id="element-1210">
                    We've made our claims process fast, transparent, and
                    hassle-free.
                  </p>

                  <button onClick={() => setStep('select')} className="bg-[#E85D3A] hover:bg-[#D04E2E] text-white font-bold py-3 px-6 rounded-xl flex items-center gap-2 transition-all active:scale-95 shadow-lg shadow-amber-500/30" data-id="element-1211">
                    <PlusIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-1212" /> File New
                    Claim
                  </button>
                </div>
              </div>

              <div data-id="element-1213">
                <div className="flex justify-between items-center mb-4 px-1" data-id="element-1214">
                  <h2 className="text-lg font-bold text-[#0D3B3B]" data-id="element-1215">
                    Your Claims
                  </h2>
                  <button className="text-xs font-bold text-gray-500 hover:text-[#0D3B3B] transition-colors" data-id="element-1216">
                    View All
                  </button>
                </div>

                <div className="space-y-4 pb-6" data-id="element-1217">
                  {existingClaims.map(claim => {
                const colors = getColorClasses(claim.colorTheme);
                return <motion.div key={claim.id} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer group" data-id="element-1218">
                        <div className="flex justify-between items-start mb-4" data-id="element-1219">
                          <div className="flex items-center gap-3" data-id="element-1220">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors.bg} ${colors.text}`} data-id="element-1221">
                              <claim.icon className="w-5 h-5" data-id="element-1222" />
                            </div>
                            <div data-id="element-1223">
                              <h3 className="font-bold text-[#0D3B3B] text-sm" data-id="element-1224">
                                {claim.product}
                              </h3>
                              <p className="text-xs text-gray-500 font-medium" data-id="element-1225">
                                {claim.id}
                              </p>
                            </div>
                          </div>
                          <div className="text-right" data-id="element-1226">
                            <span className="block font-bold text-[#0D3B3B]" data-id="element-1227">
                              {formatNaira(claim.amount)}
                            </span>
                            <span className="text-[10px] font-bold text-gray-400" data-id="element-1228">
                              {formatDate(claim.date)}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-50" data-id="element-1229">
                          <div className="flex items-center gap-2" data-id="element-1230">
                            <div className="relative flex h-2.5 w-2.5" data-id="element-1231">
                              {claim.status === 'review' && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${colors.fill}`} data-id="element-1232"></span>}
                              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${colors.fill}`} data-id="element-1233"></span>
                            </div>
                            <span className={`text-xs font-bold ${colors.textFill}`} data-id="element-1234">
                              {claim.statusText}
                            </span>
                          </div>
                          <button className="text-xs font-bold text-gray-400 group-hover:text-[#0D3B3B] transition-colors flex items-center gap-1" data-id="element-1235">
                            Track Status
                          </button>
                        </div>
                      </motion.div>;
              })}
                </div>
              </div>
            </motion.div>}

          {/* STEP 2: SELECT POLICY */}
          {step === 'select' && <motion.div key="select" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="absolute inset-0 overflow-y-auto pb-safe px-5 pt-4 flex flex-col" data-id="element-1236">
              <div className="mb-8" data-id="element-1237">
                <h1 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-1238">
                  Which plan?
                </h1>
                <p className="text-sm text-gray-500" data-id="element-1239">
                  Select the active plan you want to claim against.
                </p>
              </div>

              <div className="space-y-4 mb-8" data-id="element-1240">
                {activePolicies.map(policy => <div key={policy.id} onClick={() => setSelectedPolicy(policy.id)} className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center justify-between group ${selectedPolicy === policy.id ? 'border-amber-500 bg-amber-50/30 shadow-sm' : 'border-gray-100 bg-white hover:border-amber-200'}`} data-id="element-1241">
                    <div className="flex items-center gap-3" data-id="element-1242">
                      <div className={`w-12 h-12 rounded-full bg-${policy.color}-50 flex items-center justify-center text-${policy.color}-600`} data-id="element-1243">
                        <policy.icon className="w-6 h-6" data-id="element-1244" />
                      </div>
                      <div data-id="element-1245">
                        <h4 className="font-bold text-[#0D3B3B] text-sm" data-id="element-1246">
                          {policy.product}
                        </h4>
                        <p className="text-xs text-gray-500 font-mono mt-0.5" data-id="element-1247">
                          {policy.id}
                        </p>
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedPolicy === policy.id ? 'border-amber-500' : 'border-gray-300'}`} data-id="element-1248">
                      {selectedPolicy === policy.id && <div className="w-3 h-3 bg-amber-500 rounded-full" data-id="element-1249"></div>}
                    </div>
                  </div>)}
              </div>

              <div className="mt-auto pb-6" data-id="element-1250">
                <button disabled={!selectedPolicy} onClick={() => setStep('details')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all active:scale-95 flex items-center justify-center gap-2 ${selectedPolicy ? 'bg-[#0D3B3B] hover:bg-[#092D2D] text-white shadow-lg shadow-emerald-900/20' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-1251">
                  Continue <ArrowRightIcon className="w-5 h-5" data-id="element-1252" />
                </button>
              </div>
            </motion.div>}

          {/* STEP 3: INCIDENT DETAILS */}
          {step === 'details' && <motion.div key="details" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="absolute inset-0 overflow-y-auto pb-safe px-5 pt-4 flex flex-col" data-id="element-1253">
              <div className="mb-8" data-id="element-1254">
                <h1 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-1255">
                  What happened?
                </h1>
                <p className="text-sm text-gray-500" data-id="element-1256">
                  Provide the details of the incident for{' '}
                  {selectedPolicyDetails?.product}.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6 space-y-6" data-id="element-1257">
                <div data-id="element-1258">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-1259">
                    Date of Incident
                  </label>
                  <div className="relative" data-id="element-1260">
                    <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1261" />
                    <input type="date" value={incidentDate} onChange={e => setIncidentDate(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3.5 pl-12 pr-4 text-sm font-bold text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#0D3B3B]" data-id="element-1262" />
                  </div>
                </div>

                <div data-id="element-1263">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-1264">
                    Description of Incident
                  </label>
                  <textarea value={incidentDesc} onChange={e => setIncidentDesc(e.target.value)} placeholder="Please describe what happened in detail..." rows={5} className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#0D3B3B] resize-none" data-id="element-1265" />
                </div>
              </div>

              <div className="mt-auto pb-6" data-id="element-1266">
                <button disabled={!incidentDate || incidentDesc.length < 10} onClick={() => setStep('upload')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all active:scale-95 flex items-center justify-center gap-2 ${incidentDate && incidentDesc.length >= 10 ? 'bg-[#0D3B3B] hover:bg-[#092D2D] text-white shadow-lg shadow-emerald-900/20' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-1267">
                  Continue <ArrowRightIcon className="w-5 h-5" data-id="element-1268" />
                </button>
              </div>
            </motion.div>}

          {/* STEP 4: UPLOAD EVIDENCE */}
          {step === 'upload' && <motion.div key="upload" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="absolute inset-0 overflow-y-auto pb-safe px-5 pt-4 flex flex-col" data-id="element-1269">
              <div className="mb-8" data-id="element-1270">
                <h1 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-1271">
                  Upload Evidence
                </h1>
                <p className="text-sm text-gray-500" data-id="element-1272">
                  Please provide photos or documents to support your claim.
                </p>
              </div>

              <div className="space-y-4 mb-8" data-id="element-1273">
                <div onClick={() => setDocsUploaded(true)} className={`border-2 border-dashed rounded-3xl p-8 text-center cursor-pointer transition-all ${docsUploaded ? 'border-emerald-500 bg-emerald-50' : 'border-gray-300 bg-white hover:border-amber-500 hover:bg-amber-50/30'}`} data-id="element-1274">
                  {docsUploaded ? <div className="flex flex-col items-center" data-id="element-1275">
                      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 mb-4" data-id="element-1276">
                        <CheckCircleIcon className="w-8 h-8" data-id="element-1277" />
                      </div>
                      <h3 className="font-bold text-emerald-800 text-lg mb-1" data-id="element-1278">
                        Files Uploaded
                      </h3>
                      <p className="text-sm text-emerald-600" data-id="element-1279">
                        3 files attached successfully
                      </p>
                    </div> : <div className="flex flex-col items-center" data-id="element-1280">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 mb-4" data-id="element-1281">
                        <UploadCloudIcon className="w-8 h-8" data-id="element-1282" />
                      </div>
                      <h3 className="font-bold text-[#0D3B3B] text-lg mb-1" data-id="element-1283">
                        Tap to Upload
                      </h3>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-1284">
                        Photos, Police Reports, or Receipts
                      </p>
                      <div className="flex gap-2" data-id="element-1285">
                        <span className="bg-gray-100 text-gray-600 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1" data-id="element-1286">
                          <CameraIcon className="w-3 h-3" data-id="element-1287" /> JPG/PNG
                        </span>
                        <span className="bg-gray-100 text-gray-600 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1" data-id="element-1288">
                          <FileTextIcon className="w-3 h-3" data-id="element-1289" /> PDF
                        </span>
                      </div>
                    </div>}
                </div>

                <div className="bg-amber-50 rounded-2xl p-4 flex gap-3 border border-amber-100" data-id="element-1290">
                  <AlertCircleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-1291" />
                  <p className="text-xs text-amber-800 leading-relaxed font-medium" data-id="element-1292">
                    Clear photos and official documents help us process your
                    claim much faster.
                  </p>
                </div>
              </div>

              <div className="mt-auto pb-6" data-id="element-1293">
                <button disabled={!docsUploaded} onClick={() => setStep('review')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all active:scale-95 flex items-center justify-center gap-2 ${docsUploaded ? 'bg-[#0D3B3B] hover:bg-[#092D2D] text-white shadow-lg shadow-emerald-900/20' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-1294">
                  Review Claim <ArrowRightIcon className="w-5 h-5" data-id="element-1295" />
                </button>
              </div>
            </motion.div>}

          {/* STEP 5: REVIEW */}
          {step === 'review' && <motion.div key="review" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="absolute inset-0 overflow-y-auto pb-safe px-5 pt-4 flex flex-col" data-id="element-1296">
              <div className="mb-8" data-id="element-1297">
                <h1 className="text-2xl font-bold text-[#0D3B3B] mb-2" data-id="element-1298">
                  Review Claim
                </h1>
                <p className="text-sm text-gray-500" data-id="element-1299">
                  Please verify all details before submitting.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-1300">
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100" data-id="element-1301">
                  <div className={`w-14 h-14 rounded-2xl bg-${selectedPolicyDetails?.color}-100 flex items-center justify-center text-${selectedPolicyDetails?.color}-600 shrink-0`} data-id="element-1302">
                    {selectedPolicyDetails && <selectedPolicyDetails.icon className="w-7 h-7" data-id="element-1303" />}
                  </div>
                  <div data-id="element-1304">
                    <h3 className="font-bold text-[#0D3B3B] text-lg" data-id="element-1305">
                      {selectedPolicyDetails?.product}
                    </h3>
                    <p className="text-sm text-gray-500 font-mono" data-id="element-1306">
                      {selectedPolicyDetails?.id}
                    </p>
                  </div>
                </div>

                <div className="space-y-5" data-id="element-1307">
                  <div className="flex justify-between items-start" data-id="element-1308">
                    <span className="text-sm text-gray-500" data-id="element-1309">
                      Date of Incident
                    </span>
                    <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-1310">
                      {formatDate(incidentDate)}
                    </span>
                  </div>

                  <div className="pt-2 border-t border-gray-50 border-dashed" data-id="element-1311">
                    <span className="block text-sm text-gray-500 mb-2" data-id="element-1312">
                      Description
                    </span>
                    <p className="text-sm font-medium text-[#0D3B3B] bg-gray-50 p-3 rounded-xl leading-relaxed" data-id="element-1313">
                      {incidentDesc}
                    </p>
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t border-gray-50 border-dashed" data-id="element-1314">
                    <span className="text-sm text-gray-500" data-id="element-1315">
                      Evidence Attached
                    </span>
                    <span className="text-sm font-bold text-emerald-600 flex items-center gap-1" data-id="element-1316">
                      <CheckCircleIcon className="w-4 h-4" data-id="element-1317" /> 3 Files
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500 text-center mb-6 leading-relaxed px-4" data-id="element-1318">
                By tapping "Submit Claim", you declare that all information
                provided is true and accurate to the best of your knowledge.
              </p>

              <div className="mt-auto pb-6" data-id="element-1319">
                <button onClick={() => setStep('processing')} className="w-full py-4 rounded-xl font-bold text-lg bg-[#E85D3A] hover:bg-[#D04E2E] text-white shadow-lg shadow-amber-500/30 transition-all active:scale-95" data-id="element-1320">
                  Submit Claim
                </button>
              </div>
            </motion.div>}

          {/* STEP 6: PROCESSING */}
          {step === 'processing' && <motion.div key="processing" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50" data-id="element-1321">
              <motion.div animate={{
            rotate: 360
          }} transition={{
            repeat: Infinity,
            duration: 1,
            ease: 'linear'
          }} className="mb-6" data-id="element-1322">
                <Loader2Icon className="w-12 h-12 text-amber-500" data-id="element-1323" />
              </motion.div>
              <h2 className="text-xl font-bold text-[#0D3B3B] mb-2" data-id="element-1324">
                Submitting Claim
              </h2>
              <p className="text-sm text-gray-500" data-id="element-1325">
                Securely uploading your documents and details...
              </p>
            </motion.div>}

          {/* STEP 7: SUCCESS */}
          {step === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="absolute inset-0 overflow-y-auto pb-safe px-5 pt-10 flex flex-col bg-gradient-to-b from-[#06332E] via-[#06332E] to-[#0a4a43]" data-id="element-1326">
              <div className="text-center mb-8" data-id="element-1327">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring',
              damping: 15
            }} className="mx-auto mb-4" data-id="element-1328">
                  <img src="https://cdn.magicpatterns.com/uploads/7UEvgvkXpEAQMdiRCwr1Yk/Layer_21.png" alt="Success mascot" className="w-48 h-48 object-contain mx-auto drop-shadow-2xl" data-id="element-1329" />
                </motion.div>

                <h1 className="text-3xl font-bold text-white mb-2 tracking-tight" data-id="element-1330">
                  Claim Submitted!
                </h1>
                <p className="text-sm text-white/60 max-w-[260px] mx-auto" data-id="element-1331">
                  Your claim has been successfully submitted and is now under
                  review.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 border border-white/10 mb-8" data-id="element-1332">
                <div className="flex justify-between items-center mb-4" data-id="element-1333">
                  <span className="text-sm text-white/60" data-id="element-1334">Claim Reference</span>
                  <span className="font-mono font-bold text-white" data-id="element-1335">
                    CLM-9928
                  </span>
                </div>
                <div className="flex justify-between items-center mb-4" data-id="element-1336">
                  <span className="text-sm text-white/60" data-id="element-1337">Status</span>
                  <span className="text-xs font-bold text-[#FFD700] bg-[#FFD700]/15 px-3 py-1 rounded-full" data-id="element-1338">
                    Under Review
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1339">
                  <span className="text-sm text-white/60" data-id="element-1340">
                    Estimated Response
                  </span>
                  <span className="text-sm font-bold text-white" data-id="element-1341">48 Hours</span>
                </div>
              </div>

              <div className="mt-auto pb-6 space-y-3" data-id="element-1342">
                <button onClick={() => {
              setStep('list');
              setSelectedPolicy(null);
              setIncidentDate('');
              setIncidentDesc('');
              setDocsUploaded(false);
            }} className="w-full py-4 rounded-xl font-bold text-lg bg-[#FFD700] text-[#06332E] shadow-lg shadow-black/20 transition-all active:scale-95 hover:bg-[#FFE033]" data-id="element-1343">
                  Back to Claims
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}